
/**
 * Controller for textual menu API.
 *
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 1.7.2
 * @copyright  Copyright (C) 2021-2024, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 *    2021-12-03 (1.1.0) (jhoward)
 *      - Add content monitoring functionality to make sure the user is seeing the latest content.
 *      - Break apart the data fetch method into multiple methods for more granular use.
 *
 *    2021-12-03 (1.1.1) (jhoward)
 *      - Add href attributes to all anchors.
 *      - Update .item cards to show the "favorite" button to the right of body content rather than underneath.
 *
 *    2022-01-07 (1.2.0) (jhoward)
 *      - Refactor menu section area to its own component (<menu-sections />).
 *
 *    2022-03-23 (1.3.0) (jhoward)
 *      - <textual-menu /> now includes menu.menu_content_area_top and menu.menu_content_area_bottom.
 *
 *    2022-04-21 (1.4.0) (jhoward)
 *      - Add support for menu and menu section version numbers in textual-menu.vue.
 *      - Hide horizontal scroll indicator on small UIs in textual-menu.vue when no horizontal scroll is available.
 *
 *    2022-04-21 (1.4.1) (jhoward)
 *      - <textual-menu> now waits for document.ready to gather cached data.
 *
 *    2024-06-10 (1.4.2) (jhoward)
 *      - Add descriptive titles to links in <textual-menu>.
 *
 *    2024-06-26 (1.5.0) (jhoward)
 *      - Add support for related menus in <textual-menu>.
 *      - Add <menu-switcher> to <textual-menu>.
 *      - Add descriptive title and aria-title attributes to <textual-menu>.
 *      - Export TextualMenuComponent interface in <textual-menu>.
 *      - Code improvements in <textual-menu>.
 *
 *    2024-07-10 (1.5.1) (jhoward)
 *      - Refactor "selected menu items" button to <textual-menu>.
 *
 *    2024-07-17 (1.6.0) (jhoward)
 *      - Allow HTML in item titles and prices in <textual-menu>.
 *      - Refactor TextualMenuComponent interface from <textual-menu> to menu defs file.
 *      - Add <textual-menu>::apiHostname prop to facilitate cross-hostname communication.
 *      - Add <textual-menu>::isConstantUpdate prop to facilitate loading data once at page load.
 *      - Add <textual-menu>::isForceLatestAssets prop to facilitate not reloading the page if assets are out of sync.
 *
 *    2024-07-18 (1.6.1) (jhoward)
 *      - Add a section subtitle output to <textual-menu>.
 *
 *    2024-08-19 (1.7.0) (jhoward)
 *      - Refactor <textual-menu> to use the new <default-section> component for textual menus.
 *      - Recalculate section offsets on window.load in <textual-menu>.
 *      - Add support for menu/section/item scheduling in <textual-menu>.
 *
 *    2024-08-26 (1.7.1) (jhoward)
 *      - Use store for selected items in <textual-menu>.
 *      - Fix scroll-to-section logic in <textual-menu>.
 *      - Refactor CSS to specific components from <textual-menu>.
 *
 *    2024-09-10 (1.7.2) (jhoward)
 *      - Fix small screen UI layout in <textual-menu>.
 */

import {fetchMenuData} from '../../menu-functions';
import SearchMenu, {SearchMenuComponent} from './search-menu.vue';
import MenuSectionList from "./menu-section-list.vue";
import MenuTag from "./menu-tag.vue";
import MenuTagLegend from "./menu-tag-legend.vue";
import MenuSwitcher from "./menu-switcher.vue";
import {
  Menu,
  MenuItem,
  SectionOffsetStructure, SectionStructure, TextualMenuComponent,
  UpdateMenuResponse
} from "./defs";
import {ComponentPublicInstance, defineComponent} from 'vue';
import {methodGetNormalizedSchedules, methodIsModelScheduled} from "../../helpers/menu-sections";
import DefaultSection from "./section/textual/defaultTextualSection.vue";

export default defineComponent({
  name: "textual-menu",
  components: {
    DefaultSection,
    MenuSwitcher,
    SearchMenu,
    MenuSectionList,
    MenuTag,
    MenuTagLegend
  },
  props: {
    menuId: {
      type: Number,
      default: 0
    },
    initialSection: {
      type: Number,
      default: -1
    },
    type: {
      type: String,
      default: 'TEXTUAL'
    },
    isAdminView: {
      type: Boolean,
      default: false
    },
    apiHostname: {
      type: String,
      required: true
    },
    isConstantUpdate: {
      type: Boolean,
      default: false
    },
    isForceLatestAssets: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      currentSection: this.initialSection,
      menu: null as Menu | null,
      relatedMenus: [] as Menu[],
      menuScrollPositions: {} as { [key: number]: number },
      deviceAssetCode: '',
      isDev: !!window.pepHelperFunctions.getParameterByName('test').length,
      isLoading: false,
      pageLinkObserver: null as IntersectionObserver | null,
      disableNavButtonScrolling: false,
      disableNavButtonScrollingTimer: -1,
      windowHeight: window.innerHeight,
      showSelectedMenuItems: false,
      abortController: new AbortController(),
      abortSignal: null,

      lastContentCheck: new Date(),
      contentCheckTimeout: 60000, //1 minute
      contentCheckTimer: -1,
      contentCheckDuration: 900000, //15 minutes
      contentHash: '',

      searchIndex: '',
      searchKey: '',
      searchHost: '',

      isUserAdmin: false,

      isDocumentLoaded: 'complete' === document.readyState
    };
  },
  mounted() {
    this.initializeMenuData();
    $(this.getCachedData);

    window.addEventListener('resize', () => {
      this.windowHeight = window.innerHeight;
    }, {passive: true});
    window.addEventListener('load', () => {
      this.windowHeight = window.innerHeight;
      this.isDocumentLoaded = true;
    }, {passive: true});

    //setup datetime interval
    this.$store.commit('updateDateTimer', window.setInterval(() => {
      this.$store.commit('updateDateTime', new Date());
    }, this.$store.state.dateTimeout));
  },
  watch: {

    currentSection() {
      const currentNavButtonNode: HTMLAnchorElement | null = this.$el.querySelector('#menu_nav--btn_' + this.currentSection);
      if (currentNavButtonNode) {
        const viewportNode = currentNavButtonNode.parentElement?.parentElement;
        if (viewportNode) {
          if (viewportNode.scrollLeft > currentNavButtonNode.offsetLeft) {
            viewportNode.scrollTo({
              behavior: 'smooth',
              left: currentNavButtonNode.offsetLeft - 12
            });
          } else if (currentNavButtonNode.offsetLeft + currentNavButtonNode.clientWidth > viewportNode.scrollLeft + viewportNode.clientWidth) {
            viewportNode.scrollTo({
              behavior: 'smooth',
              left: currentNavButtonNode.offsetLeft + currentNavButtonNode.clientWidth - viewportNode.clientWidth + 12
            });
          }
        }
      }
    },
  },
  computed: {
    currentMenuId(): number {
      if (this.menu?.menu_id) {
        return this.menu.menu_id;
      }

      return 0;
    },

    allRelatedMenus(): Menu[] {
      if (this.relatedMenus.length) {
        let output = [];
        if (this.menu && (this as TextualMenuComponent).isModelScheduled(this.menu?.normalizedSchedules || [])) {
          output.push(this.menu);
        }
        this.relatedMenus.forEach(menu => {
          if ((this as TextualMenuComponent).isModelScheduled(menu?.normalizedSchedules || [])) {
            output.push(menu);
          }
        });
        output.sort((a, b) => {
          return (a?.full_title || '').localeCompare(b?.full_title || '');
        });
        return output;
      }

      return [];
    },

    sectionOffsets(): SectionOffsetStructure[] {
      this.isDocumentLoaded;
      const output: SectionOffsetStructure[] = [];
      const parentOffsetTop = this.$el.parentElement?.offsetTop || 0;
      this.menu?.sections.forEach(section => {
        (this.$refs['section--' + section.ms_id] as ComponentPublicInstance[]).forEach(sectionComponent => {
          output.push({
            el: sectionComponent.$el as HTMLElement,
            id: section.ms_id,
            offsetTop: parentOffsetTop + (sectionComponent.$el as HTMLElement).offsetTop,
            hash: sectionComponent.$el.querySelector('.page-link-group-hash')?.id || ''
          });
        });
      });

      return output;
    },

    menuItems(): { [key: number]: MenuItem } {
      const output: { [key: number]: MenuItem } = {};
      this.menu?.sections.forEach(section => {
        if (section.menu_items) {
          section.menu_items.forEach(menuItem => {
            output[menuItem.mi_id] = menuItem;
          });
        }
      });
      return output;
    },

    favoriteMenuItems(): { [key: number]: MenuItem } {
      const output: { [key: number]: MenuItem } = {};
      for (let menuItemId in this.menuItems) {
        if (-1 !== this.$store.state.selectedMenuItemIds.indexOf(Number(menuItemId))) {
          output[menuItemId] = this.menuItems[menuItemId];
        }
      }
      return output;
    },

    sectionStructures(): SectionStructure[] {
      const output: SectionStructure[] = [];
      if (this.menu?.sections) {
        this.menu.sections.forEach(section => {
          output.push({
            section: section,
            items: (section.hasOwnProperty('ms_id')) ? this.getItemsForSection(section.ms_id) : []
          });
          if (section.children) {
            section.children.forEach(childSection => {
              output.push({
                section: childSection,
                items: (childSection.ms_id) ? this.getItemsForSection(childSection.ms_id) : []
              });
            });
          }
        });
      }

      return output;
    },
  },
  methods: {

    closeSearchMenu() {
      (this.$refs.search as ComponentPublicInstance | undefined)?.$el.dispatchEvent(new CustomEvent('close'))
    },

    isModelScheduled: methodIsModelScheduled,
    getNormalizedSchedules: methodGetNormalizedSchedules,

    switchToRelatedMenu(relatedMenuId: number) {
      if (this.menu && relatedMenuId !== this.menu.menu_id) {
        let newMainMenu = null;
        let newRelatedMenus: Menu[] = [];

        this.isLoading = true;

        this.relatedMenus.forEach(relatedMenu => {
          if (relatedMenuId === relatedMenu.menu_id) {
            newMainMenu = relatedMenu;
            if (this.menu) {
              newRelatedMenus.push(this.menu);
            }
          } else {
            newRelatedMenus.push(relatedMenu);
          }
        });

        this.relatedMenus = newRelatedMenus;
        this.menu = newMainMenu;
        (this.$refs.search as SearchMenuComponent | null)?.reset();

        window.setTimeout(() => {
          //scroll to last saved position
          this.isLoading = false;
          window.scrollTo(0, this.getMenuLastScrollPosition());

          window.setTimeout(() => {
            window.resetPageLinks();
          }, 250);
        }, 500);
      }
    },

    getMenuLastScrollPosition() {
      if (this.menu && this.menuScrollPositions.hasOwnProperty(this.menu.menu_id)) {
        return this.menuScrollPositions[this.menu.menu_id];
      }

      return 0;
    },

    setupContentCheck() {
      clearTimeout(this.contentCheckTimer);
      this.contentCheckTimer = window.setTimeout(() => {
        if (this.lastContentCheck.getTime() < new Date().getTime() - this.contentCheckDuration) {
          //need to check for new data
          (this as TextualMenuComponent).getMenuData(this.menuId, this.contentHash, 'TEXTUAL', this.isDev, this.isForceLatestAssets)
            .then(this.updateMenuData)
            .then((contentUpdated) => {
              if (contentUpdated) {
                window.pepNotify.show(10000, 'Menu Updated');
              }
            })
            .finally(() => {
              //do it all over again
              this.setupContentCheck();
            });
        } else {
          this.setupContentCheck();
        }
      }, this.contentCheckTimeout);
    },

    getCachedData() {
      //look for cached selected items for this menu
      if (this.menu) {
        const cachedSelectedMenuItems = window.pepHelperFunctions.getCachedData('textual_menu--selectedMenuItems-' + this.menu.menu_id, 1000 * 60 * 60 * 24 * 90, false);
        if (cachedSelectedMenuItems) {
          if ('object' === typeof cachedSelectedMenuItems && cachedSelectedMenuItems.DATA && 'object' === typeof cachedSelectedMenuItems.DATA) {
            this.$store.commit('setSelectedMenuItemIds', cachedSelectedMenuItems.DATA);
          }
        }
      }
    },

    renderPageLinks(entries: IntersectionObserverEntry[]) {
      let visibleCount = 0;
      const localScrollReady = document.querySelector('html')?.classList.contains('scroll-ready');

      entries.forEach(function (entry) {
        const targetNode = entry.target as HTMLElement;
        if (targetNode.dataset.scrolled === 'YES') return;

        if (entry.intersectionRatio > 0.24 || (!localScrollReady && entry.intersectionRatio > 0)) {

          targetNode.classList.add('scrolled');
          targetNode.classList.add('delay-' + visibleCount++);

          targetNode.dataset.scrolled = 'YES';
        }

      });
    },

    calculateRootMarginTop() {
      return 0;//-1 * ((this.$el.querySelector('.body_container--section_nav')?.clientHeight || 0));
    },

    setupIntersectionObservers() {
      if (window.IntersectionObserver) {
        //only need to set up the observer once
        if (!this.pageLinkObserver) {
          this.pageLinkObserver = new IntersectionObserver(this.renderPageLinks, {
            root: null,	//defaults to viewport
            rootMargin: this.calculateRootMarginTop() + 'px 0px 0px 0px',
            threshold: [0, 0.1, 0.25]
          });
        }

        this.$el.querySelectorAll('.textual_menu--section .item').forEach(el => {
          this.pageLinkObserver?.observe(el);
        });
      }
    },

    updateMenuData(json: UpdateMenuResponse | null) {
      return new Promise(resolve => {
        if (json && json.STATUS === 'SUCCESS' && json.DATA.CONTENT_HASH) {

          //always update search metadata
          if (json.SETTINGS) {
            if (json.SETTINGS.SEARCH) {
              if (json.SETTINGS.SEARCH.INDEX) {
                this.searchIndex = json.SETTINGS.SEARCH.INDEX;
              }
              if (json.SETTINGS.SEARCH.KEY) {
                this.searchKey = json.SETTINGS.SEARCH.KEY;
              }
              if (json.SETTINGS.SEARCH.HOST) {
                this.searchHost = json.SETTINGS.SEARCH.HOST;
              }
            }
            if (json.SETTINGS.CONTENT_CHECK) {
              if (json.SETTINGS.CONTENT_CHECK.TIMEOUT) {
                this.contentCheckTimeout = json.SETTINGS.CONTENT_CHECK.TIMEOUT;
              }
              if (json.SETTINGS.CONTENT_CHECK.DURATION) {
                this.contentCheckDuration = json.SETTINGS.CONTENT_CHECK.DURATION;
              }
            }
            if (json.SETTINGS.USER_IS_ADMIN) {
              this.isUserAdmin = json.SETTINGS.USER_IS_ADMIN;
            }
          }

          if (this.contentHash !== json.DATA.CONTENT_HASH) {
            this.contentHash = json.DATA.CONTENT_HASH;

            if (json.DATA.MENU) {
              this.menu = this.normalizeMenuSchedules(json.DATA.MENU);
            }

            if (json.DATA.RELATED_MENUS) {
              this.relatedMenus = json.DATA.RELATED_MENUS.map((relatedMenu) => {
                return this.normalizeMenuSchedules(relatedMenu);
              });
            }

            if (json.DATA.DEVICE_ASSET_CODE) {
              this.deviceAssetCode = json.DATA.DEVICE_ASSET_CODE;
            }

            requestAnimationFrame(() => {
              this.setupIntersectionObservers();
            });
            resolve(true);
          } else {
            resolve(false);
          }
        } else {
          resolve(false);
        }
      });
    },

    normalizeMenuSchedules(menu: Menu): Menu {
      menu.normalizedSchedules = this.getNormalizedSchedules(menu.schedules || []);
      menu.items_with_tags?.forEach(item => {
        item.normalizedSchedules = this.getNormalizedSchedules(item.schedules || []);
      });

      menu.sections.map(section => {
        section.normalizedSchedules = this.getNormalizedSchedules(section.schedules || []);

        section.menu_items?.forEach(item => {
          item.normalizedSchedules = this.getNormalizedSchedules(item.schedules || []);
        });

        return section;
      });

      return menu;
    },

    initializeMenuData() {
      this.isLoading = true;

      (this as TextualMenuComponent).getMenuData(this.menuId, this.contentHash, 'TEXTUAL', this.isDev, this.isForceLatestAssets)
        .then(this.updateMenuData)
        .catch(e => {
          console.log(e);
        })
        .finally(() => {
          this.getCachedData();
          this.isLoading = false;
          if (this.isConstantUpdate) {
            this.setupContentCheck();
          }

          if (this.sectionOffsets.length) {
            this.scrollToSection(this.sectionOffsets[0], false);
          }
        });
    },

    getMenuData: fetchMenuData,

    scrollToSection(sectionOffset: SectionOffsetStructure, isSmooth = true) {
      if (this.disableNavButtonScrolling) {
        return;
      }

      this.disableNavButtonScrolling = true;
      clearTimeout(this.disableNavButtonScrollingTimer);
      this.disableNavButtonScrollingTimer = window.setTimeout(() => {
        this.disableNavButtonScrolling = false;
      }, 1000);
      window.scrollTo({
        behavior: (isSmooth) ? 'smooth' : 'auto',
        top: sectionOffset.offsetTop
      });
      this.currentSection = sectionOffset.id;
    },

    scrollToMenuItem(menuItemId: number) {
      const menuItemNode: HTMLDivElement | null = document.querySelector('#textual_menu--item-' + menuItemId);
      if (menuItemNode) {
        window.scrollTo({
          behavior: 'smooth',
          top: menuItemNode.getBoundingClientRect().top + window.scrollY + this.calculateRootMarginTop() - 32
        });

        menuItemNode.classList.add('item-highlight');
        window.setTimeout(() => {
          menuItemNode.classList.remove('item-highlight');
        }, 5000);
      }
      this.showSelectedMenuItems = false;
    },


    getItemsForSection(sectionId: number) {
      if (this.menu?.items_with_tags) {
        return this.menu.items_with_tags.filter(item => {
          return item.ms_id === sectionId;
        });
      }
      return [];
    },
  }
})
